// src/components/Quotes.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Search,
  Download,
  Send,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
  ChevronDown,
  X,
  Loader2,
  ArrowRight,
} from "lucide-react";
import {
  listQuotes,
  fetchQuote,
  createQuote,
  deleteQuote,
  sendQuote,
  acceptQuote,
  refuseQuote,
  convertQuote,
  downloadQuotePdf,
} from "../api/quotes.api";

/* ============================================================
 * Helpers
 * ============================================================ */

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function fmtMoney(n) {
  const x = Number(n || 0);
  return Number.isFinite(x) ? x.toLocaleString("fr-FR") + " XOF" : "0 XOF";
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt =
      typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
        ? new Date(d + "T00:00:00")
        : new Date(d);
    return dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(d);
  }
}

const STATUS_CONFIG = {
  DRAFT:    { label: "Brouillon",   cls: "bg-slate-100 text-slate-700 ring-slate-200" },
  SENT:     { label: "Envoyé",      cls: "bg-blue-50 text-blue-700 ring-blue-200" },
  ACCEPTED: { label: "Accepté",     cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  REFUSED:  { label: "Refusé",      cls: "bg-red-50 text-red-700 ring-red-200" },
  EXPIRED:  { label: "Expiré",      cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  INVOICED: { label: "Converti",    cls: "bg-violet-50 text-violet-700 ring-violet-200" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1", cfg.cls)}>
      {cfg.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1, 2, 3, 4, 5].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded bg-slate-200" />
        </td>
      ))}
    </tr>
  );
}

/* ============================================================
 * Modal Shell
 * ============================================================ */
function Modal({ title, onClose, children, wide = false }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className={cx("relative w-full bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]", wide ? "max-w-3xl" : "max-w-xl")}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 flex-1">{children}</div>
      </div>
    </div>
  );
}

/* ============================================================
 * Formulaire de création
 * ============================================================ */
const EMPTY_LINE = () => ({ product_name: "", qty: 1, unit_price: 0, product_sku: "" });

function CreateQuoteForm({ onSaved, onCancel }) {
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [tvaPct, setTvaPct] = useState(18);
  const [remisePct, setRemisePct] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([EMPTY_LINE()]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const subtotal = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
  const remiseAmt = Math.round((subtotal * Number(remisePct || 0)) / 100);
  const ht = subtotal - remiseAmt;
  const tva = Math.round((ht * Number(tvaPct || 0)) / 100);
  const ttc = ht + tva;

  function setLine(idx, key, val) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  }

  function addLine() {
    setLines((prev) => [...prev, EMPTY_LINE()]);
  }

  function removeLine(idx) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!buyerName.trim()) { setErr("Nom du client requis"); return; }
    if (!lines.some((l) => l.product_name.trim())) { setErr("Au moins une ligne requise"); return; }

    setSaving(true);
    setErr("");
    try {
      const result = await createQuote({
        buyer_name: buyerName.trim(),
        buyer_phone: buyerPhone || undefined,
        buyer_email: buyerEmail || undefined,
        tva_pct: Number(tvaPct),
        remise_pct: Number(remisePct),
        valid_until: validUntil || undefined,
        notes: notes || undefined,
        lines: lines
          .filter((l) => l.product_name.trim())
          .map((l) => ({
            product_name: l.product_name.trim(),
            product_sku: l.product_sku || undefined,
            qty: Number(l.qty) || 1,
            unit_price: Number(l.unit_price) || 0,
          })),
      });
      onSaved(result);
    } catch (e) {
      setErr(e?.message || "Erreur création");
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Client */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Client</h3>
        <div>
          <label className={labelCls}>Nom *</label>
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} className={inputCls} placeholder="Nom du client" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Téléphone</label>
            <input value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} className={inputCls} placeholder="+226 XX XX XX XX" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} type="email" className={inputCls} placeholder="client@email.com" />
          </div>
        </div>
      </div>

      {/* Lignes */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Lignes</h3>
        {lines.map((l, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5">
              {idx === 0 && <label className={labelCls}>Désignation *</label>}
              <input
                value={l.product_name}
                onChange={(e) => setLine(idx, "product_name", e.target.value)}
                className={inputCls}
                placeholder="Produit / service"
                required
              />
            </div>
            <div className="col-span-2">
              {idx === 0 && <label className={labelCls}>Qté</label>}
              <input
                type="number" min={1} value={l.qty}
                onChange={(e) => setLine(idx, "qty", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="col-span-3">
              {idx === 0 && <label className={labelCls}>Prix unit. HT</label>}
              <input
                type="number" min={0} step="0.01" value={l.unit_price}
                onChange={(e) => setLine(idx, "unit_price", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="col-span-2 flex justify-end">
              {lines.length > 1 && (
                <button type="button" onClick={() => removeLine(idx)} className="rounded-lg p-1.5 text-red-400 hover:bg-red-50">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="button" onClick={addLine} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
          <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
        </button>
      </div>

      {/* Paramètres */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>TVA (%)</label>
          <input type="number" min={0} max={100} step="0.1" value={tvaPct}
            onChange={(e) => setTvaPct(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Remise (%)</label>
          <input type="number" min={0} max={100} step="0.1" value={remisePct}
            onChange={(e) => setRemisePct(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Valable jusqu'au</label>
          <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={inputCls} />
        </div>
      </div>

      {/* Récap totaux */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1.5">
        {remisePct > 0 && (
          <div className="flex justify-between text-slate-500">
            <span>Sous-total brut</span><span>{fmtMoney(subtotal)}</span>
          </div>
        )}
        {remisePct > 0 && (
          <div className="flex justify-between text-red-500">
            <span>Remise ({remisePct}%)</span><span>- {fmtMoney(remiseAmt)}</span>
          </div>
        )}
        <div className="flex justify-between text-slate-600">
          <span>Total HT</span><span>{fmtMoney(ht)}</span>
        </div>
        <div className="flex justify-between text-slate-500">
          <span>TVA ({tvaPct}%)</span><span>{fmtMoney(tva)}</span>
        </div>
        <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1.5">
          <span>Total TTC</span><span>{fmtMoney(ttc)}</span>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className={labelCls}>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={cx(inputCls, "resize-none")} rows={2} placeholder="Conditions particulières, remarques..." />
      </div>

      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
          Annuler
        </button>
        <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Créer le devis
        </button>
      </div>
    </form>
  );
}

/* ============================================================
 * Panneau détail
 * ============================================================ */
function QuoteDetail({ quoteId, onClose, onRefresh }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchQuote(quoteId)
      .then((data) => { if (!cancelled) setQuote(data); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Erreur chargement"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteId]);

  async function doAction(actionFn, label, extraMsg) {
    if (!window.confirm(extraMsg || `Confirmer : ${label} ?`)) return;
    setActionLoading(label);
    try {
      await actionFn();
      await onRefresh();
      const updated = await fetchQuote(quoteId);
      setQuote(updated);
    } catch (e) {
      alert("Erreur : " + (e?.message || String(e)));
    } finally {
      setActionLoading("");
    }
  }

  async function handleConvert() {
    if (!window.confirm("Convertir ce devis en commande ? Un bon de commande sera créé.")) return;
    setActionLoading("convert");
    try {
      const result = await convertQuote(quoteId);
      await onRefresh();
      onClose();
      navigate(`/orders`);
      alert(`Commande créée : ${result.order_external_id}`);
    } catch (e) {
      alert("Erreur : " + (e?.message || String(e)));
    } finally {
      setActionLoading("");
    }
  }

  async function handlePdf() {
    setActionLoading("pdf");
    try {
      await downloadQuotePdf(quote.id, quote.external_id);
    } catch (e) {
      alert("Erreur PDF : " + (e?.message || String(e)));
    } finally {
      setActionLoading("");
    }
  }

  if (loading) return (
    <div className="flex justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  );

  if (err) return <p className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{err}</p>;
  if (!quote) return null;

  const lines = Array.isArray(quote.lines) ? quote.lines : [];
  const isLoading = (key) => actionLoading === key;

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-bold text-slate-900">{quote.external_id}</div>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={quote.status} />
            <span className="text-xs text-slate-500">{fmtDate(quote.created_at)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePdf}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {isLoading("pdf") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            PDF
          </button>
        </div>
      </div>

      {/* Client */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Client</div>
        <div className="text-sm font-semibold text-slate-900">{quote.buyer_name}</div>
        {quote.buyer_phone && <div className="text-sm text-slate-600">{quote.buyer_phone}</div>}
        {quote.buyer_email && <div className="text-sm text-slate-600">{quote.buyer_email}</div>}
        {quote.valid_until && (
          <div className="mt-2 text-xs text-amber-700 font-medium">
            Valable jusqu'au {fmtDate(quote.valid_until)}
          </div>
        )}
      </div>

      {/* Lignes */}
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">Désignation</th>
              <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">Qté</th>
              <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">Prix unit.</th>
              <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-2 text-slate-800">{l.product_name}</td>
                <td className="px-4 py-2 text-right text-slate-700">{l.qty}</td>
                <td className="px-4 py-2 text-right text-slate-700">{fmtMoney(l.unit_price)}</td>
                <td className="px-4 py-2 text-right font-medium text-slate-900">{fmtMoney(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totaux */}
      <div className="flex justify-end">
        <div className="min-w-[220px] rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1.5 text-sm">
          {Number(quote.remise_pct) > 0 && (
            <>
              <div className="flex justify-between text-slate-500">
                <span>Sous-total brut</span><span>{fmtMoney(quote.subtotal_ht)}</span>
              </div>
              <div className="flex justify-between text-red-500">
                <span>Remise ({quote.remise_pct}%)</span><span>- {fmtMoney(quote.remise_amount)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between text-slate-600">
            <span>Total HT</span><span>{fmtMoney(quote.total_ht)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>TVA ({quote.tva_pct}%)</span><span>{fmtMoney(quote.total_tva)}</span>
          </div>
          <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1.5">
            <span>Total TTC</span><span>{fmtMoney(quote.total_ttc)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {quote.notes && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          <strong className="block text-xs font-semibold uppercase tracking-wide mb-1">Notes</strong>
          {quote.notes}
        </div>
      )}

      {/* Actions selon statut */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        {quote.status === "DRAFT" && (
          <button
            onClick={() => doAction(() => sendQuote(quote.id), "send")}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading("send") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Marquer comme envoyé
          </button>
        )}
        {quote.status === "SENT" && (
          <>
            <button
              onClick={() => doAction(() => acceptQuote(quote.id), "accept")}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {isLoading("accept") ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Accepté
            </button>
            <button
              onClick={() => doAction(() => refuseQuote(quote.id), "refuse")}
              disabled={!!actionLoading}
              className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading("refuse") ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              Refusé
            </button>
          </>
        )}
        {quote.status === "ACCEPTED" && (
          <button
            onClick={handleConvert}
            disabled={!!actionLoading}
            className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {isLoading("convert") ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Convertir en commande
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * Composant principal
 * ============================================================ */
export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 30;

  // Filtres
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("date_desc");

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // Actions loading
  const [rowAction, setRowAction] = useState(null); // { id, action }

  const abortRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft), 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  // Reset pagination quand les filtres changent
  const filtersKey = `${search}|${statusFilter}|${sortFilter}`;
  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(false);
  }, [filtersKey]);

  // Charger la liste
  const fetchPage = useCallback(async ({ nextOffset = 0, append = false } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setErr("");
    try {
      const data = await listQuotes({
        search,
        status: statusFilter,
        sort: sortFilter,
        limit: LIMIT,
        offset: nextOffset,
      });
      const list = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
      setRows((prev) => (append ? [...prev, ...list] : list));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset);
    } catch (e) {
      if (String(e?.name) === "AbortError") return;
      setErr(e?.message || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sortFilter]);

  useEffect(() => {
    fetchPage({ nextOffset: 0, append: false });
  }, [fetchPage]);

  async function handleQuickAction(row, action, e) {
    e.stopPropagation();
    const key = `${row.id}:${action}`;
    if (!window.confirm(`Confirmer : ${action} pour ${row.external_id} ?`)) return;
    setRowAction(key);
    try {
      if (action === "send") await sendQuote(row.id);
      else if (action === "pdf") { await downloadQuotePdf(row.id, row.external_id); setRowAction(null); return; }
      await fetchPage({ nextOffset: 0, append: false });
    } catch (e) {
      alert("Erreur : " + (e?.message || String(e)));
    } finally {
      setRowAction(null);
    }
  }

  async function handleDelete(row, e) {
    e.stopPropagation();
    if (!window.confirm(`Supprimer le devis ${row.external_id} ?`)) return;
    setRowAction(`${row.id}:delete`);
    try {
      await deleteQuote(row.id);
      await fetchPage({ nextOffset: 0, append: false });
    } catch (e) {
      alert("Erreur : " + (e?.message || String(e)));
    } finally {
      setRowAction(null);
    }
  }

  const totalCount = rows.length;
  const totalTtc = rows.reduce((s, r) => s + Number(r.total_ttc || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Devis</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? "Chargement…" : `${totalCount} devis — total ${fmtMoney(totalTtc)}`}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nouveau devis
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Rechercher…"
            className="rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <select
          value={sortFilter}
          onChange={(e) => setSortFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="date_desc">Plus récent</option>
          <option value="date_asc">Plus ancien</option>
          <option value="total_desc">Montant ↓</option>
          <option value="buyer_az">Client A→Z</option>
        </select>

        <button
          onClick={() => fetchPage({ nextOffset: 0, append: false })}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Actualiser
        </button>
      </div>

      {/* Erreur */}
      {err && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Référence</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Validité</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Total TTC</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Statut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    <FileText className="mx-auto mb-2 h-8 w-8 opacity-40" />
                    Aucun devis trouvé
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const isActing = (action) => rowAction === `${row.id}:${action}`;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setDetailId(row.id)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700">
                        {row.external_id}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{row.buyer_name}</div>
                        {row.buyer_phone && <div className="text-xs text-slate-500">{row.buyer_phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(row.created_at)}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {row.valid_until ? fmtDate(row.valid_until) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {fmtMoney(row.total_ttc)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {/* PDF */}
                          <button
                            title="Télécharger PDF"
                            onClick={(e) => handleQuickAction(row, "pdf", e)}
                            disabled={!!rowAction}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                          >
                            {isActing("pdf") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </button>
                          {/* Envoyer (si DRAFT) */}
                          {row.status === "DRAFT" && (
                            <button
                              title="Marquer comme envoyé"
                              onClick={(e) => handleQuickAction(row, "send", e)}
                              disabled={!!rowAction}
                              className="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40"
                            >
                              {isActing("send") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </button>
                          )}
                          {/* Supprimer (DRAFT/REFUSED/EXPIRED) */}
                          {["DRAFT", "REFUSED", "EXPIRED"].includes(row.status) && (
                            <button
                              title="Supprimer"
                              onClick={(e) => handleDelete(row, e)}
                              disabled={!!rowAction}
                              className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                            >
                              {isActing("delete") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {hasMore && (
          <div className="border-t border-slate-200 px-4 py-3 text-center">
            <button
              onClick={() => fetchPage({ nextOffset: offset + LIMIT, append: true })}
              disabled={loading}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Charger plus"}
            </button>
          </div>
        )}
      </div>

      {/* Modal création */}
      {showCreate && (
        <Modal title="Nouveau devis" onClose={() => setShowCreate(false)} wide>
          <CreateQuoteForm
            onSaved={(result) => {
              setShowCreate(false);
              fetchPage({ nextOffset: 0, append: false });
              setDetailId(result.quote_id);
            }}
            onCancel={() => setShowCreate(false)}
          />
        </Modal>
      )}

      {/* Modal détail */}
      {detailId && (
        <Modal title="Détail devis" onClose={() => setDetailId(null)} wide>
          <QuoteDetail
            quoteId={detailId}
            onClose={() => setDetailId(null)}
            onRefresh={() => fetchPage({ nextOffset: 0, append: false })}
          />
        </Modal>
      )}
    </div>
  );
}
